// useCameraControls.ts - First-Person Camera Controls for Spatial OS
// WASD movement + mouse look with pointer lock

import { useRef, useCallback, useEffect, useState } from 'react'
import * as THREE from 'three'

interface CameraControlsConfig {
  moveSpeed?: number
  lookSpeed?: number
  fov?: number
}

interface CameraState {
  position: THREE.Vector3
  rotation: THREE.Euler
  direction: THREE.Vector3
}

interface UseCameraControlsReturn {
  position: THREE.Vector3
  rotation: THREE.Euler
  direction: THREE.Vector3
  update: (deltaTime: number, movementX: number, movementY: number) => void
  setPosition: (pos: THREE.Vector3) => void
  setRotation: (rot: THREE.Euler) => void
  keys: { [key: string]: boolean }
}

export function useCameraControls(
  initialPosition: THREE.Vector3 = new THREE.Vector3(0, 0, 5),
  config: CameraControlsConfig = {}
): UseCameraControlsReturn {
  const {
    moveSpeed = 5.0,
    lookSpeed = 0.002
  } = config

  // Camera state refs (for performance in animation loop)
  const positionRef = useRef(initialPosition.clone())
  const rotationRef = useRef(new THREE.Euler(0, 0, 0, 'YXZ'))
  const directionRef = useRef(new THREE.Vector3(0, 0, -1))
  
  // React state for components to subscribe to
  const [position, setPositionState] = useState(initialPosition.clone())
  const [rotation, setRotationState] = useState(new THREE.Euler(0, 0, 0, 'YXZ'))
  const [direction, setDirectionState] = useState(new THREE.Vector3(0, 0, -1))

  // Key states
  const keysRef = useRef<{ [key: string]: boolean }>({
    w: false,
    a: false,
    s: false,
    d: false,
    Shift: false,
    ' ': false
  })

  // Set position externally
  const setPosition = useCallback((pos: THREE.Vector3) => {
    positionRef.current.copy(pos)
    setPositionState(pos.clone())
  }, [])

  // Set rotation externally
  const setRotation = useCallback((rot: THREE.Euler) => {
    rotationRef.current.copy(rot)
    setRotationState(rot.clone())
  }, [])

  // Update camera based on input
  const update = useCallback((deltaTime: number, movementX: number, movementY: number) => {
    // Apply mouse look (only if pointer is locked)
    if (document.pointerLockElement) {
      rotationRef.current.y -= movementX * lookSpeed
      rotationRef.current.x -= movementY * lookSpeed
      
      // Clamp vertical look to prevent flipping
      rotationRef.current.x = Math.max(-Math.PI / 2 + 0.1, Math.min(Math.PI / 2 - 0.1, rotationRef.current.x))
    }

    // Calculate forward direction based on rotation (Three.js camera faces -Z by default)
    const forward = new THREE.Vector3(
      -Math.sin(rotationRef.current.y),
      0,
      -Math.cos(rotationRef.current.y)
    ).normalize()

    // Calculate right direction (perpendicular to forward)
    const right = new THREE.Vector3(
      Math.cos(rotationRef.current.y),
      0,
      -Math.sin(rotationRef.current.y)
    ).normalize()

    // Calculate movement speed (sprint with Shift)
    const currentSpeed = keysRef.current.Shift ? moveSpeed * 2 : moveSpeed
    const moveDistance = currentSpeed * deltaTime

    // Apply WASD movement
    if (keysRef.current.w) {
      positionRef.current.addScaledVector(forward, moveDistance)
    }
    if (keysRef.current.s) {
      positionRef.current.addScaledVector(forward, -moveDistance)
    }
    if (keysRef.current.a) {
      positionRef.current.addScaledVector(right, -moveDistance)
    }
    if (keysRef.current.d) {
      positionRef.current.addScaledVector(right, moveDistance)
    }

    // Vertical movement (Space = up, not implemented for ground-based movement)
    // Could add crouch with Ctrl if needed

    // Update direction vector
    directionRef.current.set(
      Math.sin(rotationRef.current.y) * Math.cos(rotationRef.current.x),
      Math.sin(rotationRef.current.x),
      Math.cos(rotationRef.current.y) * Math.cos(rotationRef.current.x)
    ).normalize()

    // Update React state
    setPositionState(positionRef.current.clone())
    setRotationState(rotationRef.current.clone())
    setDirectionState(directionRef.current.clone())
  }, [moveSpeed, lookSpeed])

  // Keyboard event handlers
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // INPUT GUARD - Don't process camera keys if user is typing in an input field
      const activeTag = document.activeElement?.tagName
      const isInInput = activeTag === 'INPUT' || activeTag === 'TEXTAREA'
      
      // If in input, don't process camera keys
      if (isInInput) {
        return
      }
      
      // Prevent default for game keys so they don't accidentally type
      const key = e.key.toLowerCase()
      if (['w', 'a', 's', 'd', ' ', 'shift'].includes(key)) {
        e.preventDefault()
      }

      if (keysRef.current.hasOwnProperty(key) || keysRef.current.hasOwnProperty(e.key)) {
        keysRef.current[key] = true
        keysRef.current[e.key] = true
      }

      // Exit pointer lock on Escape
      if (e.key === 'Escape') {
        document.exitPointerLock()
      }
    }

    const handleKeyUp = (e: KeyboardEvent) => {
      // INPUT GUARD - Don't process camera keys if user is typing in an input field
      const activeTag = document.activeElement?.tagName
      if (activeTag === 'INPUT' || activeTag === 'TEXTAREA') {
        return
      }

      const key = e.key.toLowerCase()
      if (keysRef.current.hasOwnProperty(key) || keysRef.current.hasOwnProperty(e.key)) {
        keysRef.current[key] = false
        keysRef.current[e.key] = false
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
    }
  }, [])

  return {
    position,
    rotation,
    direction,
    update,
    setPosition,
    setRotation,
    keys: keysRef.current
  }
}

export default useCameraControls
